import React, { useState, useEffect } from "react";
import {
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Grid2,
  Button,
  Autocomplete,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useDebounce from "./useDebounce";
import {
  IconCalendarWeek,
  IconDownload,
  IconFilterOff,
  IconPlus,
  IconSearch,
} from "@tabler/icons-react";
import { lightPalette } from "../theme-layout/palette";

const GridComponent = ({
  columns,
  getGridData,
  gridKey,
  isRequiredAdvanceFilter = false,
  addButtonTitle, onAdd, isAddButton, isDownloadButton, addDownloadTitle, onDownload, downloadLoading,
  advanceFiltersConfig = [],
}) => {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortModel, setSortModel] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isApiCalled, setIsApiCalled] = useState(false);
  const debouncedSearch = useDebounce(searchText, 1000);
  const [advanceFiltersState, setAdvanceFiltersState] = useState({});
  const [advanceFiltersValue, setAdvanceFiltersValue] = useState({});
  const [isShowAdvanceFilter, setIsShowAdvanceFilter] = useState(false);
  const debouncedFilter = useDebounce(advanceFiltersState, 300);

  const handleFilterChange = (name, value, isNeedApiCalled = true) => {
    if (isNeedApiCalled) {
      setAdvanceFiltersState((prev) => ({ ...prev, [name]: value }));
    }
    setAdvanceFiltersValue((prev) => ({ ...prev, [name]: value }));
  };

  const loadData = async () => {
    try {
      const response = await getGridData({
        page: page + 1,
        pageSize,
        sortModel,
        searchText,
        filters: advanceFiltersState,
      });
      setRows(response.items || []);
      setRowCount(response.totalCount || 0);
    } catch (error) {
      console.error("Failed to fetch grid data:", error);
    } finally {
      setIsApiCalled(true)
    }
  };

  useEffect(() => {
    loadData();
  }, [page, pageSize, sortModel, debouncedSearch, gridKey, debouncedFilter]);

  const renderFilter = (filter) => {
    const { type, label, name, options = [] } = filter;
    const value = advanceFiltersValue[name] || "";
    switch (type) {
      case "text":
        return (
          <Grid2 item size={{ xs: 6, sm: 4, md: 2.5 }} key={name}>
            <TextField
              label={label}
              size="small"
              value={value}
              onChange={(e) => handleFilterChange(name, e.target.value)}
              fullWidth
            />
          </Grid2>
        );

      case "select":
        return (
          <Grid2 item size={{ xs: 6, sm: 4, md: 2.5 }} key={name}>
            <Autocomplete
              options={options}
              size="small"
              value={options.find((opt) => opt.name === value) || null}
              onChange={(_, newValue) =>
                handleFilterChange(name, newValue?.name ?? "")
              }
              getOptionLabel={(option) => option.name || ""}
              renderInput={(params) => <TextField {...params} label={label} />}
              isOptionEqualToValue={(option, val) => option?.id === val?.id}
              getOptionKey={(option) => option?.id}
              fullWidth
            />
          </Grid2>
        );

      case "date-range":
        const fromDate = value?.from || "";
        const toDate = value?.to || "";
        const isValidRange =
          fromDate && toDate && dayjs(fromDate) <= dayjs(toDate);
        const showError = fromDate && toDate && !isValidRange;
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs} key={name}>
            <Grid2
              item
              size={{ xs: 12, sm: 8, md: 5 }}
              display="flex"
              alignItems="stretch"
              gap={2}
            >
              <DatePicker
                label={`${label} From`}
                value={value?.from ? value.from : null}
                onChange={(newValue) => {
                  const from = newValue ? newValue : "";
                  const updated = { ...value, from };
                  setAdvanceFiltersValue((prev) => ({
                    ...prev,
                    [name]: updated,
                  }));
                  if (from && value?.to && from <= dayjs(value?.to)) {
                    handleFilterChange(name, updated);
                  }
                }}
                sx={{ width: "100%" }}
                slotProps={{
                  textField: {
                    size: "small",
                    placeholder: "MM/DD/YYYY",
                    fullWidth: false,
                    readOnly: true,
                    error: showError,
                  },
                }}
                slots={{
                  openPickerIcon: () => <IconCalendarWeek stroke={1.5} />,
                }}
              />
              <DatePicker
                label={`${label} To`}
                value={value?.to ? dayjs(value.to) : null}
                onChange={(newValue) => {
                  const to = newValue ? newValue : "";
                  const updated = { ...value, to };
                  setAdvanceFiltersValue((prev) => ({
                    ...prev,
                    [name]: updated,
                  }));
                  if (value?.from && to && value?.from <= dayjs(to)) {
                    handleFilterChange(name, updated);
                  }
                }}
                sx={{ width: "100%" }}
                slotProps={{
                  textField: {
                    size: "small",
                    placeholder: "MM/DD/YYYY",
                    fullWidth: false,
                    readOnly: true,
                    error: showError,
                    helperText: showError
                      ? "To date must be after From date"
                      : "",
                  },
                }}
                slots={{
                  openPickerIcon: () => <IconCalendarWeek stroke={1.5} />,
                }}
              />
              {fromDate !== "" && toDate !== "" && (
                <IconButton
                  onClick={() => handleFilterChange(name, "", !showError)}
                >
                  <IconFilterOff />
                </IconButton>
              )}
            </Grid2>
          </LocalizationProvider>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    setPage(0);
  }, [debouncedFilter]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      flex="1 1 0"
      gap={3}
      overflow="auto"
      minHeight={350}
    >

      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2}>
        <TextField
          placeholder="Search"
          variant="outlined"
          size="small"
          fullWidth
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(0);
          }}
          sx={{ m: "0 0 0 auto", width: "auto", minWidth: 264 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <IconSearch
                    stroke={1.5}
                    color={lightPalette.secondary.light}
                  />
                </InputAdornment>
              ),
            },
          }}
        />
        {isAddButton && (
          <Button
            variant="contained"
            startIcon={<IconPlus stroke={1.5} />}
            onClick={onAdd} // Call the passed prop
          >
            {addButtonTitle || "Add New"}
          </Button>
        )}
        {isRequiredAdvanceFilter && (
          <Button
            variant="outlined"
            onClick={() => setIsShowAdvanceFilter(!isShowAdvanceFilter)}
          >
            Advanced Filter
          </Button>
        )}
        {isDownloadButton && (
          <Button
            variant="contained"
            startIcon={
              downloadLoading
                ? <CircularProgress size={16} color="inherit" />
                : <IconDownload stroke={1.5} />
            }
            onClick={onDownload}
            disabled={downloadLoading}
          >
            {downloadLoading ? "Downloading..." : (addDownloadTitle || "Download")}
          </Button>
        )}

      </Box>
      <Grid2 container flexWrap="wrap" spacing={2}>
        {isShowAdvanceFilter &&
          advanceFiltersConfig.map((filter) => renderFilter(filter))}
      </Grid2>
      <Box width="100%" overflow="auto" flex="1 1 0">
        <DataGrid
          disableColumnMenu
          isRowSelectable={() => false}
          rows={rows}
          columns={columns}
          rowCount={rowCount}
          paginationMode="server"
          sortingMode="server"
          paginationModel={{ page, pageSize }}
          onPageChange={(newPage) => setPage(newPage)}
          columnHeaderHeight={48}
          getRowHeight={() => "auto"}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          slots={{
            noRowsOverlay: () => (
              <Typography
                sx={{ mt: 4, textAlign: "center", color: "text.secondary" }}
              >
                {isApiCalled ? "No records found." : ""}
              </Typography>
            ),
          }}
          pageSizeOptions={[10, 20, 50]}
          onSortModelChange={(model) => {
            setSortModel(model);
            setPage(0);
          }}
          sx={{
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.25)",
            border: "1px solid rgba(255, 255, 255, 0.5)",
            boxShadow: "0px 0px 15px rgba(204, 204, 204, 0.15)",
            backdropFilter: "blur(5px)",
            borderRadius: "8px",
            minWidth: 1000,

            ".MuiDataGrid-main": {
              flex: "1 1 0",
              overflow: "auto",

              ".MuiDataGrid-virtualScroller": {
                overflow: "auto !important",

                ".MuiDataGrid-columnHeaders": {
                  ".MuiDataGrid-columnHeader": {
                    padding: 1.5,
                    "--DataGrid-rowBorderColor": lightPalette.primary.lighter,

                    ".MuiDataGrid-columnHeaderTitle": {
                      fontSize: 14,
                      fontWeight: 400,
                      color: lightPalette.default.main,
                    },

                    ".MuiIconButton-sizeSmall": {
                      paddingTop: 0,
                      paddingBottom: 0,
                    },

                    ".MuiDataGrid-columnSeparator": {
                      visibility: "hidden",
                    },
                  },

                  ":hover": {
                    ".MuiDataGrid-columnHeader": {
                      ".MuiDataGrid-columnSeparator": {
                        visibility: "visible",
                      },
                    },
                  },
                },

                ".MuiDataGrid-row": {
                  "--rowBorderColor": lightPalette.primary.lightest1,

                  "&.MuiDataGrid-row--firstVisible": {
                    "--rowBorderColor": lightPalette.primary.lighter,
                  },

                  ".MuiDataGrid-cell": {
                    padding: 1.5,
                    lineHeight: 1.25,
                    fontSize: 14,
                    fontWeight: 400,
                    color: lightPalette.common.dataCell,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,

                    ":focus": {
                      outline: "none",
                    },

                    ".MuiIconButton-sizeSmall": {
                      paddingTop: 0,
                      paddingBottom: 0,
                    },

                    ".MuiButton-sizeSmall": {
                      lineHeight: "normal",
                      borderRadius: 1,
                    },

                    ":focus-within": {
                      outline: "none",
                    }
                  },

                  "&:last-child": {
                    borderColor: "transparent",
                  },
                },

                ".MuiDataGrid-filler": {
                  "> div": {
                    border: "none",
                  },
                },
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default GridComponent;
